import { useState } from 'react';

import { UseMutateAsyncFunction, UseMutateFunction, UseMutationOptions, UseMutationResult } from './types';

// This is a simple version of the`useMutation` hook from `@tanstack/react-query`.
// https://tanstack.com/query/latest/docs/framework/react/reference/useMutation
// We don't need everything `@tanstack/react-query` provides, so there isn't enough reason
// to add the entire library as a dependency. This is a simple version that works for our use case.

export const useMutation = <
  TData = unknown,
  TError = unknown,
  TVariables = void,
>(options: UseMutationOptions<TData, TError, TVariables>): UseMutationResult<TData, TError, TVariables> => {
  const [data, setData] = useState<null | TData>(null);
  const [error, setError] = useState<null | TError>(null);
  const [isError, setIsError] = useState(false);
  const [isIdle, setIsIdle] = useState(true);
  const [isPending, setIsPending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [status, setStatus] = useState<'error' | 'idle' | 'loading' | 'success'>('idle');

  const handleStartMutation = () => {
    setData(null);
    setError(null);
    setIsError(false);
    setIsIdle(false);
    setIsPending(true);
    setIsSuccess(false);
    setStatus('loading');
  };

  const handleSuccess = (response: TData) => {
    setData(response);
    setError(null);
    setIsError(false);
    setIsIdle(false);
    setIsPending(false);
    setIsSuccess(true);
    setStatus('success');
  };

  const handleError = (err: TError) => {
    setError(err);
    setIsError(true);
    setIsIdle(false);
    setIsPending(false);
    setIsSuccess(false);
    setStatus('error');
  };

  const handleSettled = () => {
    setIsIdle(false);
    setIsPending(false);
  };

  const mutateAsync: UseMutateAsyncFunction<TData, TError, TVariables> = async (variables, opts) => {
    handleStartMutation();

    let internalData = data;
    let internalError = error;

    try {
      await options.onMutate?.(variables);
      const response = await options.mutationFn(variables);
      internalData = response;
      handleSuccess(response);
      opts?.onSuccess?.(response, variables);
      await options.onSuccess?.(response, variables);

      return response;
    }
    catch (err) {
      internalError = err as TError;
      handleError(err as TError);
      opts?.onError?.(err as TError, variables);
      await options.onError?.(err as TError, variables);

      return internalData as NonNullable<TData>;
    }
    finally {
      handleSettled();
      await options.onSettled?.(internalData, internalError, variables);
    }
  };

  const mutate: UseMutateFunction<TData, TError, TVariables> = (variables, opts) => {
    void mutateAsync(variables, opts);
  };

  // @ts-expect-error data type is correct here, the union type is not being inferred correctly
  return {
    data,
    error,
    isError,
    isIdle,
    isPending,
    isSuccess,
    mutate,
    mutateAsync,
    status,
  };
};
